import { ofetch } from 'ofetch'
import type { CustomerConfig, SignedIdentity, WidgetError, WidgetFieldError } from './types'

/**
 * How stale (in seconds) `userHashTimestamp` is allowed to be before we call the host's
 * `refreshIdentity` callback. 3 minutes against the default 10-minute backend TTL leaves
 * enough headroom that clock skew between the host server and the backend, a slow refresh
 * endpoint, or a just-woke-from-suspend tab don't cause the signature to land stale. If
 * the signature still expires mid-flight, we auto-retry once after forcing a refresh — see
 * `widgetFetch` below.
 */
const STALENESS_THRESHOLD_SECONDS = 3 * 60

/**
 * Minimum gap between identical `console.warn` lines emitted when `refreshIdentity()` throws.
 * A broken host endpoint shouldn't spam the console every 200ms on a queued burst.
 */
const REFRESH_WARN_DEDUPE_MS = 60_000

/**
 * Proper Error subclass so devtools stack traces are useful and `instanceof Error` checks work.
 * Carries the normalized `WidgetError` surface (`code`, `message`, `status`, `errors`) as own
 * properties — callers that previously caught `WidgetError` keep working unchanged because the
 * shape is a strict superset.
 */
export class WidgetFetchError extends Error implements WidgetError {
  public readonly code: string
  public readonly status?: number
  public readonly errors?: WidgetFieldError[]

  constructor(init: WidgetError) {
    super(init.message)
    this.name = 'WidgetFetchError'
    this.code = init.code
    this.status = init.status
    this.errors = init.errors
  }
}

function parseWidgetErrorBody(body: Record<string, unknown> | undefined, status: number, statusText: string): WidgetError {
  const rawErrors = Array.isArray(body?.errors) ? body!.errors as Array<Record<string, unknown>> : []
  const fieldErrors: WidgetFieldError[] = rawErrors.map((e) => {
    const pointer = typeof e.source === 'object' && e.source !== null
      ? (e.source as Record<string, unknown>).pointer as string | undefined
      : undefined
    const field = pointer?.startsWith('/data/attributes/')
      ? pointer.slice('/data/attributes/'.length)
      : pointer?.split('/').filter(Boolean).pop()
    return {
      code: (e.code as string) ?? 'UNKNOWN',
      detail: (e.detail as string) ?? '',
      pointer,
      field,
    }
  })
  return {
    code: (body?.responseCode as string) ?? `HTTP_${status}`,
    message: (body?.responseMessage as string) ?? statusText,
    status,
    errors: fieldErrors.length > 0 ? fieldErrors : undefined,
  }
}

let apiKey = ''
let baseUrl = ''
let oidcTokenProvider: (() => Promise<{ accessToken: string }>) | null = null
let currentCustomer: CustomerConfig | null = null

/**
 * Single in-flight refresh promise so parallel `onRequest` invocations during a refresh don't
 * stampede the host endpoint. Cleared once the promise settles.
 */
let inFlightRefresh: Promise<void> | null = null
let lastRefreshWarnAt = 0

export function configureWidgetClient(url: string, key: string) {
  baseUrl = url.replace(/\/$/, '')
  apiKey = key
}

export function setOidcTokenProvider(provider: (() => Promise<{ accessToken: string }>) | null) {
  oidcTokenProvider = provider
}

/**
 * Records the customer the host app has identified. When the customer has a `userHash`, subsequent
 * requests automatically carry the `X-Widget-User-Signature` and `X-Widget-User-Timestamp` headers
 * so the backend can verify the host-asserted identity. A new customer resets the refresh
 * dedupe state so a stale in-flight refresh can't clobber a freshly-set signature.
 */
export function setWidgetCustomer(customer: CustomerConfig | null): void {
  currentCustomer = customer
  inFlightRefresh = null
}

function isSignatureStale(): boolean {
  const ts = currentCustomer?.userHashTimestamp
  if (typeof ts !== 'number') return true // no timestamp yet → refresh as soon as a callback is set
  const now = Math.floor(Date.now() / 1000)
  return now - ts > STALENESS_THRESHOLD_SECONDS
}

function isValidRefreshResult(result: unknown): result is SignedIdentity {
  if (!result || typeof result !== 'object') return false
  const candidate = result as Partial<SignedIdentity>
  return typeof candidate.userHash === 'string'
    && candidate.userHash.length > 0
    && typeof candidate.userHashTimestamp === 'number'
    && Number.isFinite(candidate.userHashTimestamp)
}

/**
 * Call the host's `refreshIdentity` callback and patch `currentCustomer` in place. Swallows
 * errors (logged once per minute) so a broken host endpoint can't stall request dispatch —
 * the stale signature falls through and the backend rejects it cleanly with 401
 * `INVALID_SIGNATURE`, which the global MutationCache/QueryCache handler surfaces as a toast.
 */
async function refreshSignedIdentity(): Promise<void> {
  const customer = currentCustomer
  const callback = customer?.refreshIdentity
  if (!customer || !callback) return

  if (inFlightRefresh) {
    await inFlightRefresh
    return
  }

  inFlightRefresh = (async () => {
    try {
      const result = await callback()
      if (!isValidRefreshResult(result)) {
        throwRefreshWarning('refreshIdentity() returned an invalid SignedIdentity (expected { userHash: string, userHashTimestamp: number })')
        return
      }

      // Reject a refresh result whose timestamp is already past the staleness threshold at the
      // moment of receipt. This catches the "host endpoint took 4+ minutes to respond" edge
      // case and the "host caches the sign-identity response" anti-pattern — either way the
      // fresh signature would land at the server already half-expired. Skipping the mutation
      // keeps the previous (still-possibly-valid) pair in place; the subsequent request's
      // INVALID_SIGNATURE 401 triggers the automatic retry with another refresh attempt.
      const nowSeconds = Math.floor(Date.now() / 1000)
      const ageSeconds = nowSeconds - result.userHashTimestamp
      if (ageSeconds > STALENESS_THRESHOLD_SECONDS) {
        throwRefreshWarning(`refreshIdentity() returned a pair already ${ageSeconds}s old (threshold ${STALENESS_THRESHOLD_SECONDS}s) — check host-app clock skew and cache headers`)
        return
      }

      // Mutate in place so setWidgetCustomer references stay valid. The host never reads these
      // fields directly — only this module does — so in-place mutation is safe.
      customer.userHash = result.userHash
      customer.userHashTimestamp = result.userHashTimestamp
    } catch (err) {
      throwRefreshWarning(err)
    } finally {
      inFlightRefresh = null
    }
  })()

  await inFlightRefresh
}

/**
 * Force a refresh on the next outbound request by zero-ing the cached timestamp and clearing
 * any in-flight refresh. Used by the 401-retry path so a re-dispatch doesn't silently reuse
 * the same stale signature that just got rejected.
 */
function forceNextRequestToRefresh(): void {
  if (currentCustomer) currentCustomer.userHashTimestamp = 0
  inFlightRefresh = null
}

function throwRefreshWarning(reason: unknown): void {
  const now = Date.now()
  if (now - lastRefreshWarnAt < REFRESH_WARN_DEDUPE_MS) return
  lastRefreshWarnAt = now
  console.warn('[reqdesk-widget] refreshIdentity() failed; falling back to stale signature:', reason)
}

async function applySignedIdentityHeaders(headers: Headers): Promise<void> {
  // Refresh before reading. If the host didn't provide a callback, we just use whatever
  // setWidgetCustomer was last given — backwards-compatible with hosts on 1.2.12 and earlier.
  if (currentCustomer?.refreshIdentity && isSignatureStale()) {
    await refreshSignedIdentity()
  }

  if (!currentCustomer?.userHash) return
  headers.set('X-Widget-User-Signature', currentCustomer.userHash)
  if (typeof currentCustomer.userHashTimestamp === 'number') {
    headers.set('X-Widget-User-Timestamp', String(currentCustomer.userHashTimestamp))
  }
  // Email rides along with the signature so GET endpoints (which have no body for the verifier
  // to inspect) can verify the HMAC over `{ts}.{email}` server-side. POST endpoints continue to
  // read the body email; sending it here as well is harmless — the verifier canonicalizes both.
  if (currentCustomer.email) {
    headers.set('X-Widget-User-Email', currentCustomer.email)
  }
}

const innerWidgetFetch = ofetch.create({
  timeout: 15000,
  retry: false,

  async onRequest({ options }) {
    options.baseURL = `${baseUrl}/api/v1`
    const headers = new Headers(options.headers as HeadersInit)
    headers.set('X-API-Key', apiKey)

    if (oidcTokenProvider) {
      try {
        const tokens = await oidcTokenProvider()
        if (tokens?.accessToken) {
          headers.set('Authorization', `Bearer ${tokens.accessToken}`)
        } else {
          console.warn('[reqdesk-widget] OIDC token provider returned empty accessToken', tokens)
        }
      } catch (err) {
        console.warn('[reqdesk-widget] Failed to get OIDC access token:', err)
      }
    }

    await applySignedIdentityHeaders(headers)

    options.headers = headers
  },

  onResponseError({ response }) {
    const body = response._data as Record<string, unknown> | undefined
    throw new WidgetFetchError(parseWidgetErrorBody(body, response.status, response.statusText))
  },
})

type WidgetFetch = typeof innerWidgetFetch

/**
 * Public fetch wrapper. Auto-retries exactly once on a `401 INVALID_SIGNATURE` response by
 * forcing a fresh `refreshIdentity()` call and re-dispatching the same request. This closes
 * the race window where:
 *   - the widget's staleness check (`STALENESS_THRESHOLD_SECONDS`) thought the sig was fresh,
 *   - but by the time the request landed on the backend the server clock had moved past the
 *     project's `SignedIdentityTtlMinutes` window (clock skew / tab throttling / just-woke-up
 *     from suspend / a slow refresh endpoint returning an already-aged pair).
 *
 * The retry only fires when a `refreshIdentity` callback is configured. Hosts that don't
 * provide one see the same one-shot failure behaviour as before — no silent endless retries.
 * Any non-`INVALID_SIGNATURE` error propagates on the first attempt.
 */
export const widgetFetch = (async (
  request: Parameters<WidgetFetch>[0],
  options?: Parameters<WidgetFetch>[1] & { __signatureRetried?: boolean },
) => {
  try {
    return await innerWidgetFetch(request, options as Parameters<WidgetFetch>[1])
  } catch (err) {
    const shouldRetry =
      err instanceof WidgetFetchError
      && err.status === 401
      && err.code === 'INVALID_SIGNATURE'
      && !!currentCustomer?.refreshIdentity
      && !options?.__signatureRetried
    if (!shouldRetry) throw err

    // Force a fresh refresh on the retry; clears the in-flight promise so a queued refresh
    // from a different request doesn't short-circuit the fresh call.
    forceNextRequestToRefresh()
    await refreshSignedIdentity()
    return await innerWidgetFetch(request, { ...(options ?? {}), __signatureRetried: true } as Parameters<WidgetFetch>[1])
  }
}) as WidgetFetch

// Upload helper using XHR for progress tracking (ofetch doesn't support upload progress)
export async function uploadWithProgress(
  path: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<unknown> {
  // Refresh the signed identity before opening the XHR so the upload carries a fresh HMAC —
  // matches the fetch path exactly. Single await, no header-injection timing surprise.
  if (currentCustomer?.refreshIdentity && isSignatureStale()) {
    await refreshSignedIdentity()
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${baseUrl}${path}`)
    xhr.setRequestHeader('X-API-Key', apiKey)

    // Attach signed-identity headers so uploads from a verified customer stay verified.
    if (currentCustomer?.userHash) {
      xhr.setRequestHeader('X-Widget-User-Signature', currentCustomer.userHash)
      if (typeof currentCustomer.userHashTimestamp === 'number') {
        xhr.setRequestHeader('X-Widget-User-Timestamp', String(currentCustomer.userHashTimestamp))
      }
      if (currentCustomer.email) {
        xhr.setRequestHeader('X-Widget-User-Email', currentCustomer.email)
      }
    }

    if (oidcTokenProvider) {
      oidcTokenProvider()
        .then(tokens => {
          xhr.setRequestHeader('Authorization', `Bearer ${tokens.accessToken}`)
          sendXhr()
        })
        .catch(() => sendXhr())
    } else {
      sendXhr()
    }

    function sendXhr() {
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
        })
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)) } catch { resolve({}) }
        } else {
          let body: Record<string, unknown> | undefined
          try { body = JSON.parse(xhr.responseText) as Record<string, unknown> } catch { /* ignore */ }
          reject(new WidgetFetchError(parseWidgetErrorBody(body, xhr.status, xhr.statusText)))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new WidgetFetchError({
          code: 'NETWORK_ERROR',
          message: 'Network error during upload',
        }))
      })

      const form = new FormData()
      form.append('file', file)
      xhr.send(form)
    }
  })
}
